import type { z } from 'zod';

import { apiClient } from '@/lib/api-client';
import { ApiError } from '@/lib/errors';

import {
  callRowSchema,
  claimCallResultSchema,
  dialCallResultSchema,
  dialOptionsSchema,
  didMappingSchema,
  telephonyAgentSchema,
  type CallListParams,
  type ClaimCallInput,
  type ClaimCallResult,
  type DialCallInput,
  type DialCallResult,
  type DialOptions,
  type DidMapping,
  type DidMappingInput,
  type DismissReason,
  type PaginatedCalls,
  type TelephonyAgent,
  type TelephonyAgentInput,
  type UpdateDidMappingInput,
  type UpdateTelephonyAgentInput,
} from '../types/schemas';

// parseOrThrow and stripEmptyStrings are per-service local helpers by
// convention (clientsService, redFlagsService each carry their own copy), not
// shared exports. Copied from clientsService.ts.
function parseOrThrow<T>(schema: z.ZodType<T>, raw: unknown, context: string): T {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') ?? '';
    const detail = first
      ? `${path ? `${path}: ` : ''}${first.message}`
      : 'Unexpected response shape';
    if (import.meta.env.DEV) {
      console.error(`[telephonyService] ${context} response failed schema parse:`, {
        issues: result.error.issues,
        raw,
      });
    }
    throw new ApiError({
      status: 0,
      code: 'SHAPE_MISMATCH',
      message: `${context}: ${detail}`,
    });
  }
  return result.data;
}

// Drops '' and undefined only. Booleans survive, so `isCrisis: false` reaches
// the server as an explicit false rather than being treated as "unset".
function stripEmptyStrings<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === '' || v === undefined) continue;
    (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

export const telephonyService = {
  /** GET /telephony/calls. Scoped server-side to the caller's locations. */
  async listCalls(params: CallListParams = {}): Promise<PaginatedCalls> {
    const { data } = await apiClient.get<{
      data: unknown[];
      pagination?: { page: number; limit: number; total: number };
    }>('/telephony/calls', { params });
    const rows = data.data.map((row) => parseOrThrow(callRowSchema, row, 'listCalls'));
    return {
      data: rows,
      pagination: data.pagination ?? { page: 1, limit: rows.length, total: rows.length },
    };
  },

  /**
   * POST /telephony/calls/:id/claim. Names a new caller or attaches a matched
   * client, then opens the session. 409 `Already claimed by <name>` on a lost
   * race; the hook refetches the pool either way.
   */
  async claimCall({ id, input }: { id: string; input: ClaimCallInput }): Promise<ClaimCallResult> {
    const payload = stripEmptyStrings({
      clientId: input.clientId,
      firstName: input.firstName,
      lastName: input.lastName,
      isCrisis: input.isCrisis,
    });
    const { data } = await apiClient.post<{ data: unknown }>(
      `/telephony/calls/${id}/claim`,
      payload,
    );
    return parseOrThrow(claimCallResultSchema, data.data, 'claimCall');
  },

  /** POST /telephony/calls/:id/dismiss. Removes the call from the pending pool. */
  async dismissCall({ id, reason }: { id: string; reason: DismissReason }): Promise<void> {
    await apiClient.post(`/telephony/calls/${id}/dismiss`, { reason });
  },

  /** POST /telephony/calls/:id/reopen. Puts a dismissed call back in the pool. */
  async reopenCall(id: string): Promise<void> {
    await apiClient.post(`/telephony/calls/${id}/reopen`);
  },

  /** POST /telephony/calls/:id/reassign. Moves a claimed call to another practitioner. */
  async reassignCall({
    id,
    practitionerId,
  }: {
    id: string;
    practitionerId: string;
  }): Promise<void> {
    await apiClient.post(`/telephony/calls/${id}/reassign`, { practitionerId });
  },

  /** GET /telephony/dial-options. The agent roster, with live state where the feed knows it. */
  async getDialOptions(): Promise<DialOptions> {
    const { data } = await apiClient.get<{ data: unknown }>('/telephony/dial-options');
    return parseOrThrow(dialOptionsSchema, data.data, 'getDialOptions');
  },

  /**
   * POST /telephony/dial. Ozonetel rings the chosen agent, then the number;
   * the session is already created, assigned to the caller, when this
   * returns. 409 carries the vendor's refusal in plain words.
   */
  async dialCall(input: DialCallInput): Promise<DialCallResult> {
    const { data } = await apiClient.post<{ data: unknown }>(
      '/telephony/dial',
      stripEmptyStrings({ ...input }),
    );
    return parseOrThrow(dialCallResultSchema, data.data, 'dialCall');
  },

  /** GET /telephony/agents. The roster the click-to-call picker offers. */
  async listAgents(): Promise<TelephonyAgent[]> {
    const { data } = await apiClient.get<{ data: unknown[] }>('/telephony/agents');
    return data.data.map((row) => parseOrThrow(telephonyAgentSchema, row, 'listAgents'));
  },

  /** POST /telephony/agents. */
  async createAgent(input: TelephonyAgentInput): Promise<TelephonyAgent> {
    const { data } = await apiClient.post<{ data: unknown }>(
      '/telephony/agents',
      stripEmptyStrings(input),
    );
    return parseOrThrow(telephonyAgentSchema, data.data, 'createAgent');
  },

  /** PATCH /telephony/agents/:id. Partial, including the active toggle. */
  async updateAgent({
    id,
    input,
  }: {
    id: string;
    input: UpdateTelephonyAgentInput;
  }): Promise<TelephonyAgent> {
    const { data } = await apiClient.patch<{ data: unknown }>(
      `/telephony/agents/${id}`,
      stripEmptyStrings(input),
    );
    return parseOrThrow(telephonyAgentSchema, data.data, 'updateAgent');
  },

  /** DELETE /telephony/agents/:id. Soft delete, so the id can be re-added. */
  async deleteAgent(id: string): Promise<void> {
    await apiClient.delete(`/telephony/agents/${id}`);
  },

  /** GET /telephony/did-mappings. BA-scoped for workspace admins, global for platform. */
  async listDidMappings(): Promise<DidMapping[]> {
    const { data } = await apiClient.get<{ data: unknown[] }>('/telephony/did-mappings');
    return data.data.map((row) => parseOrThrow(didMappingSchema, row, 'listDidMappings'));
  },

  /** POST /telephony/did-mappings. Returns 201 with the created row. */
  async createDidMapping(input: DidMappingInput): Promise<DidMapping> {
    const { data } = await apiClient.post<{ data: unknown }>(
      '/telephony/did-mappings',
      stripEmptyStrings(input),
    );
    return parseOrThrow(didMappingSchema, data.data, 'createDidMapping');
  },

  /** PATCH /telephony/did-mappings/:id. Partial update, including the active toggle. */
  async updateDidMapping({
    id,
    input,
  }: {
    id: string;
    input: UpdateDidMappingInput;
  }): Promise<DidMapping> {
    const { data } = await apiClient.patch<{ data: unknown }>(
      `/telephony/did-mappings/${id}`,
      stripEmptyStrings(input),
    );
    return parseOrThrow(didMappingSchema, data.data, 'updateDidMapping');
  },

  /** DELETE /telephony/did-mappings/:id. */
  async deleteDidMapping(id: string): Promise<void> {
    await apiClient.delete(`/telephony/did-mappings/${id}`);
  },
};
